import { BrowserRouter, Route, Routes, Navigate } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/react";

import { queryClient } from "@studio/lib/queries/query-client";
import { getEnv } from "./lib/utils";
import UserSessionTracker from "@studio/lib/components/UserSessionTracker";
import AuthGuard from "./features/AuthGuard";
import Dashboard from "./features/Dashboard";
import AdminAuthGuard from "./features/AdminAuthGuard";
import ApiKeys from "./features/ApiKeys";
import IntercomSync from "./features/IntercomSync";
import AddCredits from "./features/AddCredits";
import AddRole from "./features/AddRole";
import InviteUser from "./features/InviteUser";
import CopyOverlayTemplate from "./features/CopyOverlayTemplate";
import SyncBilling from "./features/SyncBilling";
import TeamMemberships from "./features/TeamMemberships";
import ManageInvites from "./features/ManageInvites";
import ManageTeamMembers from "./features/ManageTeamMembers";
import BlockRules from "./features/BlockRules";
import Fonts from "./features/Fonts";
import BackgroundMusicPeaks from "./features/BackgroundMusicPeaks";
import SignInPage from "./features/SignIn";

declare global {
  interface Window {
    Clerk: any;
  }
}

export const adminPaths = {
  dashboard: () => "/",
  apiKeys: () => "/api-keys",
  intercomSync: () => "/intercom-sync",
  addCredits: () => "/add-credits",
  syncBilling: () => "/sync-billing",
  addRole: () => "/add-role",
  inviteUser: () => "/invite-user",
  copyOverlayTemplate: () => "/copy-overlay-template",
  teamMemberships: () => "/team-memberships",
  manageInvites: () => "/manage-invites",
  manageTeamMembers: () => "/manage-team-members",
  blockRules: () => "/block-rules",
  fonts: () => "/fonts",
  backgroundMusicPeaks: () => "/background-music-peaks",
  signIn: () => "/sign-in",
};

export default function AdminApp() {
  return (
    <ClerkProvider
      publishableKey={getEnv("VITE_CLERK_PUBLISHABLE_KEY")}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <UserSessionTracker />
          <Routes>
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/" element={<AuthGuard />}>
              <Route path="/" element={<AdminAuthGuard />}>
                <Route index element={<Dashboard />} />
                <Route path="api-keys" element={<ApiKeys />} />
                <Route path="intercom-sync" element={<IntercomSync />} />
                <Route path="add-credits" element={<AddCredits />} />
                <Route path="sync-billing" element={<SyncBilling />} />
                <Route path="add-role" element={<AddRole />} />
                <Route path="invite-user" element={<InviteUser />} />
                <Route
                  path="copy-overlay-template"
                  element={<CopyOverlayTemplate />}
                />
                <Route path="team-memberships" element={<TeamMemberships />} />
                <Route path="manage-invites" element={<ManageInvites />} />
                <Route
                  path="manage-team-members"
                  element={<ManageTeamMembers />}
                />
                <Route path="block-rules" element={<BlockRules />} />
                <Route path="fonts" element={<Fonts />} />
                <Route
                  path="background-music-peaks"
                  element={<BackgroundMusicPeaks />}
                />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ClerkProvider>
  );
}
